import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AppConfig } from 'AppConfig';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as ReportActions from 'features/frontoffice/actions/actions';
import * as ReportTypes from 'features/frontoffice/actions/types';
import * as FavoriteReportActions from 'features/frontoffice/favoriteReport/actions/action';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { store } from 'store/store';
import { hasPermission } from 'utils/common';
import { formatReptSource } from 'utils/formatReptSource';
import { trackClick, trackingActions, trackingTypes } from 'utils/tracking';
import * as Constant from 'common/Constant';

const useStyles = makeStyles((theme) => ({
  box: {
    display: '-webkit-box',
    boxOrient: 'vertical',
    lineClamp: 2,
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'block',
    overflow: 'hidden'
  }
}));

const ReportCard = ({ item, showFavorite = false, showDismiss = false, showTitle = true, showThumbnail = false, reportType }) => {
  const classes = useStyles();
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const tableauServiceUrl = useSelector((state) => state.user.tableauServiceUrl);

  const navigate = useNavigate();
  const location = useLocation();

  // Hide favorite icon for Custom Reports, Exclusive Reports, and My Creation
  const shouldShowFavorite = showFavorite && 
    reportType !== AppConfig.CUSTOM_CAROUSEL && 
    reportType !== AppConfig.EXCLUSIVE_CAROUSEL && 
    reportType !== AppConfig.AD_HOC_CAROUSEL &&
    item?.category !== AppConfig.REPORT_CATEGORY.CUSTOM.code &&
    item?.category !== AppConfig.REPORT_CATEGORY.AD_HOC.code;

  const handleReportClick = () => {
    if (item?.id) {
      trackClick(
        trackingTypes.REPORT,
        trackingActions.READ,
        item.id,
        item.category + ';' + item.displayText
      );
    }
    const reportBaseUrl = location?.pathname;
    const [workbook, dashboard] = item.reptSource.split('/');
    const reportOriginComponent = Constant.REPORTS;
    navigate(`${reportBaseUrl}/${currentTenantCd}/view/${workbook}/${dashboard}`);

    store.dispatch({
      type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
      reportUrl: item.reportSource
        ? item.reportSource
        : formatReptSource(tableauServiceUrl, currentTenantCd, item.reptSource),
      category: item.category,
      title: item.displayText,
      reportBaseUrl,
      report: item,
      reportOriginComponent
    });
    sessionStorage.setItem('reportOrigin', JSON.stringify({ reportBaseUrl, reportOriginComponent }));
  };

  const addFavorite = (id) => {
    store.dispatch(FavoriteReportActions.addFavoriteReport(id, reportType));
  };

  const deleteFavorite = (id) => {
    store.dispatch(FavoriteReportActions.deleteFavoriteReport(id, reportType));
  };

  const handleFavorite = () => {
    item.favorited ? deleteFavorite(item.id) : addFavorite(item.id);
  };

  const dismiss = () => {
    confirmDialog(
      () => store.dispatch(ReportActions.dismissReport(item.id)),
      'Are you sure you want to dismiss this report?',
      'Dismiss Report'
    );
  };

  return (
    <>
      <Card style={{ width: AppConfig.HOME_SMART_CARD_WIDTH }}>
        {showTitle ? (
          <Tooltip title={<h6>{item.displayText}</h6>}>
            <div>
              <CardHeader
                className={classes.cardHeader}
                title={
                  <Typography noWrap gutterBottom variant="h6" component="h4" sx={{ color: 'rgba(0,0,0, 0.87)' }}>
                    {item.displayText}
                  </Typography>
                }
              />
            </div>
          </Tooltip>
        ) : (
          <CardHeader className={classes.cardHeader} />
        )}
        {showThumbnail && (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <img
              height="150px"
              width="auto"
              src={item.s3Url}
              alt="No Thumbnail"
            />
          </Box>
        )}
        <CardContent style={{ height: '50px' }}>
          <Box className={classes.box}>
            {showTitle === true ? (
              <Tooltip title={<h6>{item.description}</h6>}>
                <Typography>{item.description}</Typography>
              </Tooltip>
            ) : (
              <Typography
                style={{
                  height: '50px',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                {item.description}
              </Typography>
            )}
          </Box>
        </CardContent>
        <CardActions disableSpacing>
          {shouldShowFavorite === true && (
            <Tooltip
              title={
                item.favorited
                  ? 'Remove from my favorites'
                  : 'Mark as a favorite'
              }>
              <IconButton 
              onClick={handleFavorite} 
              size="large" 
              disabled={item.favoriteDisabled}
              >
                <FavoriteIcon
                 color={item.favorited ? item.favoriteDisabled ? "disabled" : '#ED2C2D' : null }
                />
              </IconButton>
            </Tooltip>
          )}
          {showDismiss === true &&
            hasPermission(
              AppConfig.PERMISSION.FO_SMARTCARD_DISMISS,
              permissions
            ) && (
            <Tooltip title="Dismiss">
              <IconButton onClick={dismiss} size="large">
                <ThumbDownIcon />
              </IconButton>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReportClick}
            sx={{    
              color: 'rgb(63, 81, 181)',
              borderColor: 'rgb(53, 81, 181)'
            }}
            style={{ marginLeft: 'auto' }}>
            View Report
          </Button>
        </CardActions>
      </Card>
    </>
  );
};

export default ReportCard;
